/**
 * APIサーバへのアクセス用
 */

import axios, { type AxiosResponse } from "axios";
import {
  type Category,
  type Comment,
  type DbReaction,
  Domain,
  type IconReaction,
  type Message,
  type Topic,
  User,
} from "@/model";
import type {
  Category as DbCategory,
  Comment as DbComment,
  Department as DbDepartment,
  Message as DbMessage,
  Topic as DbTopic,
  Department,
} from "./API";
import type { ServerUserInfo } from "./aws-config";
import type {
  PdfAttachmentFileResponse,
  PdfConverterParam,
  PdfConverterStatusResponse,
} from "./components/pdf-converter-utility";
import type {
  DirectDomainMembersType,
  DirectTalksType,
} from "./direct-restapi-types";
import type { DomainSettings } from "./domain-setting-types";
import { EventManager } from "./events/event-manager";
import type { SolutionLinkResponse } from "./model/solution";
import sentry from "./sentry";
import type { PersonalSetting, SORT_TYPE } from "./store";
import type { FileSignedUrl } from "./store/file-store";

export type Domains = Direct.GetDomains;

// API-GWへの接続設定
const PRODUCT_BASE = import.meta.env.VITE_PRODUCT_BASE || "/";
const AUTH_SERVER_URL =
  import.meta.env.VITE_AUTH_SERVER_URL || "http://localhost:3000/";

const ServerApi = {
  userData: new URL(`${PRODUCT_BASE}userData`, AUTH_SERVER_URL).toString(),
  flow: new URL(`${PRODUCT_BASE}flow`, AUTH_SERVER_URL).toString(),
  domainMembers: (domainId: string, offset?: number, limit?: number) => {
    const url = new URL(`${PRODUCT_BASE}domain-members`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    if (offset) params.append("offset", String(offset));
    if (limit) params.append("limit", String(limit));
    return url.toString();
  },
  listCategories: (domainId: string) => {
    const url = new URL(`${PRODUCT_BASE}list-categories`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    return url.toString();
  },
  topicListByAcl: (domainId: string) => {
    const url = new URL(`${PRODUCT_BASE}list-topics-by-acl`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    return url.toString();
  },
  listMessages: (domainId: string, topicId: string, messageId?: string) => {
    const url = new URL(`${PRODUCT_BASE}list-messages`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    params.append("topic_id", topicId);
    if (messageId) {
      params.append("message_id", messageId);
    }
    return url.toString();
  },
  listComments: (
    domainId: string,
    topicId: string,
    messageId?: string,
    sort?: SORT_TYPE,
  ) => {
    const url = new URL(`${PRODUCT_BASE}list-comments`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    params.append("topic_id", topicId);
    if (messageId) {
      params.append("message_id", messageId);
    }
    if (sort) {
      params.append("sort", `${sort}`);
    }
    return url.toString();
  },
  listReactions: (domainId: string, topicId?: string) => {
    const url = new URL(`${PRODUCT_BASE}list-reactions`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    if (topicId) {
      params.append("topic_id", topicId);
    }
    return url.toString();
  },
  listDepartments: (domainId: string) => {
    const url = new URL(`${PRODUCT_BASE}list-departments`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    return url.toString();
  },
  listApps: (domainId: string) => {
    const url = new URL(`${PRODUCT_BASE}list-apps`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    return url.toString();
  },
  listDomains: () => {
    const url = new URL(`${PRODUCT_BASE}list-domains`, AUTH_SERVER_URL);
    return url.toString();
  },
  listUsers: (domainId: string) => {
    const url = new URL(`${PRODUCT_BASE}list-users`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    return url.toString();
  },
  getMe: () => {
    const url = new URL(`${PRODUCT_BASE}get-me`, AUTH_SERVER_URL);
    return url.toString();
  },
  getProfileIcon: (directId: string) => {
    const url = new URL(`${PRODUCT_BASE}get-profile-icon`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("direct_id", directId);
    return url.toString();
  },
  getFile: (
    pathParam: {
      domainId: string;
      topicId: string;
      messageId?: string;
      commentId?: string;
    },
    fileName: string,
    thumb: boolean,
    contentType: string,
  ) => {
    const url = new URL(`${PRODUCT_BASE}get-file`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", pathParam.domainId);
    params.append("topic_id", pathParam.topicId);
    if (pathParam.messageId) {
      params.append("message_id", pathParam.messageId);
    }
    if (pathParam.commentId) {
      params.append("comment_id", pathParam.commentId);
    }
    params.append("file_name", fileName);
    params.append("thumb", thumb ? "true" : "false");
    params.append("content_type", contentType);
    return url.toString();
  },
  deleteFiles: (
    domainId: string,
    topicId: string,
    files: { messageId?: string; commentId?: string; fileName: string }[],
  ) => {
    const url = new URL(`${PRODUCT_BASE}delete-files`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    params.append("topic_id", topicId);
    const paramStr = JSON.stringify(files);
    params.append("files", paramStr);
    return url.toString();
  },
  getDomainSettings: (domainId: string) => {
    const url = new URL(`${PRODUCT_BASE}get-domain-settings`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    return url.toString();
  },
  listFeatures: () => {
    const url = new URL(`${PRODUCT_BASE}list-features`, AUTH_SERVER_URL);
    return url.toString();
  },
  getHelpContent: () => {
    const url = new URL(`${PRODUCT_BASE}get-help-content`, AUTH_SERVER_URL);
    return url.toString();
  },
  getPersonalSetting: () => {
    const url = new URL(`${PRODUCT_BASE}get-personal-setting`, AUTH_SERVER_URL);
    return url.toString();
  },
  getTalks: (domainId: string) => {
    const url = new URL(`${PRODUCT_BASE}get-talks`, AUTH_SERVER_URL);
    const params = url.searchParams;
    params.append("domain_id", domainId);
    return url.toString();
  },
  upsertDepartments: new URL(
    `${PRODUCT_BASE}upsert-departments`,
    AUTH_SERVER_URL,
  ).toString(),
  createCategory: new URL(
    `${PRODUCT_BASE}create-category`,
    AUTH_SERVER_URL,
  ).toString(),
  updateCategory: new URL(
    `${PRODUCT_BASE}update-category`,
    AUTH_SERVER_URL,
  ).toString(),
  createTopic: new URL(
    `${PRODUCT_BASE}create-topic`,
    AUTH_SERVER_URL,
  ).toString(),
  updateTopic: new URL(
    `${PRODUCT_BASE}update-topic`,
    AUTH_SERVER_URL,
  ).toString(),
  createMessage: new URL(
    `${PRODUCT_BASE}create-message`,
    AUTH_SERVER_URL,
  ).toString(),
  updateMessage: new URL(
    `${PRODUCT_BASE}update-message`,
    AUTH_SERVER_URL,
  ).toString(),
  createComment: new URL(
    `${PRODUCT_BASE}create-comment`,
    AUTH_SERVER_URL,
  ).toString(),
  updateComment: new URL(
    `${PRODUCT_BASE}update-comment`,
    AUTH_SERVER_URL,
  ).toString(),
  upsertReaction: new URL(
    `${PRODUCT_BASE}upsert-reaction`,
    AUTH_SERVER_URL,
  ).toString(),
  upsertDomainSettings: new URL(
    `${PRODUCT_BASE}upsert-domain-settings`,
    AUTH_SERVER_URL,
  ).toString(),
  logging: new URL(`${PRODUCT_BASE}logging`, AUTH_SERVER_URL).toString(),
  notifications: new URL(
    `${PRODUCT_BASE}notifications`,
    AUTH_SERVER_URL,
  ).toString(),
  topicMessageCount: new URL(
    `${PRODUCT_BASE}topic/update-message-count`,
    AUTH_SERVER_URL,
  ).toString(),
  putFiles: new URL(`${PRODUCT_BASE}put-files`, AUTH_SERVER_URL).toString(),
  upsertPersonalSetting: new URL(
    `${PRODUCT_BASE}upsert-personal-setting`,
    AUTH_SERVER_URL,
  ).toString(),
  revokeToken: new URL(
    `${PRODUCT_BASE}oauth2/revoke-token`,
    AUTH_SERVER_URL,
  ).toString(),
  getPdfConverterStatus: new URL(
    `${PRODUCT_BASE}pdf-converter`,
    AUTH_SERVER_URL,
  ).toString(),
  sendStartPdfConversion: new URL(
    `${PRODUCT_BASE}pdf-converter/convert`,
    AUTH_SERVER_URL,
  ).toString(),
  getPdfAttachmentFile: new URL(
    `${PRODUCT_BASE}pdf-converter/attachment`,
    AUTH_SERVER_URL,
  ).toString(),

  error: new URL(`${PRODUCT_BASE}debug-sentry`, AUTH_SERVER_URL).toString(),
};

export class ServerApiAccess {
  // APIのレスポンスに対してイベント処理を行う
  private handleResponse<T>(
    response: AxiosResponse<T> | undefined,
    apiLabel: string,
  ): T | undefined {
    if (!response) {
      console.log("response is undefined. or not axios error.");
      return undefined;
    }

    const status = response.status;
    if (status === 200 || status === 204) {
      // 問題なし
      return response.data;
    } else if (status === 401) {
      // 再認証: ログアウト処理
      EventManager.logoutEvent();
      return undefined;
    } else if (status === 403) {
      // アクセス権なし
      ServerApiAccess.errorlog(apiLabel, 403, response);
      EventManager.accessDeinedAlert(String(response.data));
      return undefined;
    } else if (status === 404) {
      // 見つからない
      ServerApiAccess.errorlog(apiLabel, 404, response);
      EventManager.notFoundAlert(String(response.data));
      return undefined;
    } else if (status === 429) {
      // レート制限
      ServerApiAccess.errorlog(apiLabel, 429, response);
      EventManager.networkErrorAlert();
      return undefined;
    } else if (status === 500) {
      // サーバーエラー
      ServerApiAccess.errorlog(apiLabel, 500, response);
      EventManager.networkErrorAlert();
      return undefined;
    } else {
      ServerApiAccess.errorlog(apiLabel, undefined, response);
      return undefined;
    }
  }

  /**
   * GET系 API にアクセス
   * @param api API-GW REST API endpoint
   */
  private async getApi<T>(
    api: string,
    apiLabel: string,
  ): Promise<T | undefined> {
    try {
      const response = await axios.get<T>(api, { withCredentials: true });
      return this.handleResponse(response, apiLabel);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorResponse = err?.response;
        return this.handleResponse(errorResponse, apiLabel);
      } else {
        return this.handleResponse(undefined, apiLabel);
      }
    }
  }

  /**
   * POST系 API にアクセス
   * @param api API-GW REST API endpoint
   */
  private async postApi<T>(
    api: string,
    param: any,
    apiLabel: string,
  ): Promise<T | undefined> {
    try {
      const response = await axios.post<T>(api, param, {
        withCredentials: true,
      });
      return this.handleResponse(response, apiLabel);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorResponse = err?.response;
        return this.handleResponse(errorResponse, apiLabel);
      } else {
        return this.handleResponse(undefined, apiLabel);
      }
    }
  }

  /**
   * PUT系 API にアクセス
   * @param api API-GW REST API endpoint
   * @param param パラメーター
   */
  private async putApi<T>(
    api: string,
    param: any,
    apiLabel: string,
  ): Promise<T | undefined> {
    try {
      const response = await axios.put<T>(api, param, {
        withCredentials: true,
      });
      return this.handleResponse(response, apiLabel);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorResponse = err?.response;
        return this.handleResponse(errorResponse, apiLabel);
      } else {
        return this.handleResponse(undefined, apiLabel);
      }
    }
  }

  /**
   * PUT系 API にアクセス
   * @param api API-GW REST API endpoint
   */
  private async deleteApi<T>(
    api: string,
    apiLabel: string,
  ): Promise<T | undefined> {
    try {
      const response = await axios.delete(api, { withCredentials: true });
      return this.handleResponse(response, apiLabel);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorResponse = err?.response;
        return this.handleResponse(errorResponse, apiLabel);
      } else {
        return this.handleResponse(undefined, apiLabel);
      }
    }
  }

  /**
   * 必須チェック
   * @param params パラメータ
   * @param apiLabel ラベル
   * @returns
   */
  private validateRequiredParams(
    params: Record<string, any>,
    apiLabel: string,
  ): boolean {
    const values = Object.values(params);
    for (const value of values) {
      if (value === undefined || value === null || value === "") {
        console.error(`${apiLabel} validate required params error.`, params);
        return false;
      }
    }
    return true;
  }

  /**
   * パラメーターのキーを変換（トップレベルのみ）
   * @param param パラメーター
   * @returns
   */
  private convertKeysCamelToSnake(
    param: Record<string, any>,
  ): Record<string, any> {
    const result: Record<string, any> = {};
    for (const key in param) {
      if (Object.hasOwn(param, key)) {
        // キャメルケースのキーをスネークケースに変換
        const snakeKey = key.replace(
          /([A-Z])/g,
          (letter) => `_${letter.toLowerCase()}`,
        );
        result[snakeKey] = param[key];
      }
    }
    return result;
  }

  /**
   * /userData にアクセスして、自分のユーザー情報を取得します
   * @returns ユーザー情報。number: 401は未ログインエラー。undefined: その他のエラー
   */
  public async getUserData(): Promise<ServerUserInfo | number | undefined> {
    try {
      const response = await axios.get<{ user: ServerUserInfo }>(
        ServerApi.userData,
        { withCredentials: true },
      );

      if (response.status === 200) {
        const user = response.data.user as ServerUserInfo;
        return user;
      } else if (response.status === 401) {
        return 401;
      } else {
        return undefined;
      }
    } catch (err) {
      console.log("get user data error: ", err);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          return 401;
        }
      }
      return undefined;
    }
  }

  /**
   * 組織内メンバー一覧を取得する
   * @param domainId 組織ID
   * @param offset オフセット
   * @param limit リミット
   * @returns
   */
  public async getDomainMembers(
    domainId: string,
    offset: number = 0,
    limit: number = 20,
  ): Promise<DirectDomainMembersType | number | undefined> {
    const apiLabel = "getDomainMembers";
    const requiredParams = { domainId };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.getApi<DirectDomainMembersType>(
      ServerApi.domainMembers(domainId, offset, limit),
      apiLabel,
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * 話題の投稿数を更新する
   * @param domainId 組織ID
   * @param topicId 話題ID
   * @param updateMessageCount true: 話題の投稿数を更新する
   */
  public async putTopicMessageCount(
    domainId: string,
    topicId: string,
    updateMessageCount: boolean,
  ): Promise<void> {
    const apiLabel = "putTopicMessageCount";
    const requiredParams = { domainId, topicId };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.putApi<any>(
      ServerApi.topicMessageCount,
      {
        domain_id: domainId,
        topic_id: topicId,
        update_message_count: updateMessageCount,
      },
      apiLabel,
    );
    return result;
  }

  /**
   * 話題取得
   * @param domainId
   * @returns
   */
  public async getTopics(
    domainId: string,
  ): Promise<{ topics: DbTopic[]; totalCount: number } | undefined> {
    const apiLabel = "getTopics";
    const requiredParams = { domainId };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.getApi<{ topics: DbTopic[]; totalCount: number }>(
      ServerApi.topicListByAcl(domainId),
      apiLabel,
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  // API共通処理
  private async _apiAccess<T>(
    params: string,
    apiLabel: string,
  ): Promise<T | undefined> {
    const result = await this.getApi<T>(params, apiLabel);
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /** directのアプリ一覧を取得する */
  public async listApps(
    domainId: string,
  ): Promise<SolutionLinkResponse | undefined | number> {
    const apiLabel = "listApps";
    const requiredParams = { domainId };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const params = ServerApi.listApps(domainId);
    const result = await this._apiAccess<SolutionLinkResponse>(
      params,
      apiLabel,
    );
    return result;
  }

  /** 部署の更新を行う */
  public async upsertDepartments(
    domainId: string,
  ): Promise<DbDepartment[] | undefined> {
    const apiLabel = "upsertDepartments";
    const requiredParams = { domainId };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.putApi<DbDepartment[]>(
      ServerApi.upsertDepartments,
      { domain_id: domainId },
      apiLabel,
    );
    return result;
  }

  /** 部署情報を取得 */
  public async listDepartments(domainId: string): Promise<Department[]> {
    const apiLabel = "listDepartments";
    const requiredParams = { domainId };
    if (!this.validateRequiredParams(requiredParams, apiLabel)) return [];

    const result = await this.getApi<Department[]>(
      ServerApi.listDepartments(domainId),
      apiLabel,
    );
    if (result) {
      return result;
    } else {
      return [];
    }
  }

  /** directの組織情報を取得 */
  public async listDomains(): Promise<Domains | undefined> {
    const params = ServerApi.listDomains();
    const result = await this._apiAccess<Domains>(params, "listDomains");
    if (!result || result.length === 0) {
      EventManager.logoutEvent();
      return undefined;
    }
    return result;
  }

  /**
   * カテゴリー作成
   * @param category
   * @returns
   */
  public async createCategory(
    category: Category,
  ): Promise<DbCategory | undefined> {
    const apiLabel = "createCategory";
    const requiredParams = { domainId: category.domainId, id: category.id };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake(category);
    const result = await this.putApi<DbCategory>(
      ServerApi.createCategory,
      bodyParams,
      apiLabel,
    );
    return result;
  }

  /**
   * カテゴリー一覧取得
   * @param domainId
   * @returns
   */
  public async listCategories(
    domainId: string,
  ): Promise<DbCategory[] | undefined> {
    const apiLabel = "listCategories";
    const requiredParams = { domainId };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.getApi<DbCategory[]>(
      ServerApi.listCategories(domainId),
      apiLabel,
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * カテゴリー編集
   * @param category
   * @returns
   */
  public async updateCategory(
    category: Partial<Category>,
  ): Promise<DbCategory | undefined> {
    const apiLabel = "updateCategory";
    const requiredParams = { domainId: category.domainId, id: category.id };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake(category);
    const result = await this.putApi<DbCategory>(
      ServerApi.updateCategory,
      bodyParams,
      apiLabel,
    );
    return result;
  }

  /**
   * 話題作成
   * @param topic
   * @returns
   */
  public async createTopic(
    topic: Topic,
    notifyOnCreate: boolean,
  ): Promise<DbTopic | undefined> {
    const apiLabel = "createTopic";
    const requiredParams = { domainId: topic.domainId, id: topic.id };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake({
      ...topic,
      notify_on_create: notifyOnCreate,
    });
    const result = await this.putApi<DbTopic>(
      ServerApi.createTopic,
      bodyParams,
      apiLabel,
    );
    // 無料版の制限エラー
    if (
      typeof result === "string" &&
      (result as string).match(/exceeded limit/)
    ) {
      EventManager.freeAlertEvent();
      return undefined;
    } else if (result) {
      return result;
    } else {
      ServerApiAccess.errorlog("createTopic", result);
      return undefined;
    }
  }

  /**
   * 話題更新
   * @param topic
   * @returns
   */
  public async updateTopic(
    topic: Partial<Topic>,
  ): Promise<DbTopic | undefined> {
    const apiLabel = "updateTopic";
    const requiredParams = { domainId: topic.domainId, id: topic.id };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake(topic);
    const result = await this.putApi<DbTopic>(
      ServerApi.updateTopic,
      bodyParams,
      apiLabel,
    );
    return result;
  }

  /**
   * 投稿作成
   * @param message
   * @returns
   */
  public async createMessage(
    message: Message,
  ): Promise<
    | { message: DbMessage; thumbs: FileSignedUrl[]; data: FileSignedUrl[] }
    | undefined
  > {
    const apiLabel = "createMessage";
    const requiredParams = {
      domainId: message.domainId,
      topicId: message.topicId,
      id: message.id,
    };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake(message);
    const result = await this.putApi<{
      message: DbMessage;
      thumbs: FileSignedUrl[];
      data: FileSignedUrl[];
    }>(ServerApi.createMessage, bodyParams, apiLabel);
    // 無料版の制限エラー
    if (
      typeof result === "string" &&
      (result as string).match(/exceeded limit/)
    ) {
      EventManager.freeAlertEvent();
      return undefined;
    } else if (result) {
      return result;
    } else {
      ServerApiAccess.errorlog("createMessage", result);
      return undefined;
    }
  }

  /**
   * 投稿編集
   * @param message
   * @returns
   */
  public async updateMessage(
    message: Partial<Message>,
  ): Promise<
    | { message: DbMessage; thumbs: FileSignedUrl[]; data: FileSignedUrl[] }
    | undefined
  > {
    const apiLabel = "updateMessage";
    const requiredParams = {
      domainId: message.domainId,
      topicId: message.topicId,
      id: message.id,
    };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake(message);
    const result = await this.putApi<{
      message: DbMessage;
      thumbs: FileSignedUrl[];
      data: FileSignedUrl[];
    }>(ServerApi.updateMessage, bodyParams, apiLabel);
    return result;
  }

  /**
   * 投稿一覧取得
   * @param domainId
   * @param topicId
   * @param messageId
   * @returns
   */
  public async listMessages(
    domainId: string,
    topicId: string,
    messageId?: string,
  ): Promise<{ messages: DbMessage[] } | undefined> {
    const apiLabel = "listMessages";
    const requiredParams = { domainId, topicId };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.getApi<{ messages: DbMessage[] }>(
      ServerApi.listMessages(domainId, topicId, messageId),
      apiLabel,
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * コメント作成
   * @param comment
   * @returns
   */
  public async createComment(
    comment: Comment,
  ): Promise<
    | { comment: DbComment; thumbs: FileSignedUrl[]; data: FileSignedUrl[] }
    | undefined
  > {
    const apiLabel = "createComment";
    const requiredParams = {
      domainId: comment.domainId,
      topicId: comment.topicId,
      messageId: comment.messageId,
      id: comment.id,
    };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake(comment);
    const result = await this.putApi<{
      comment: DbComment;
      thumbs: FileSignedUrl[];
      data: FileSignedUrl[];
    }>(ServerApi.createComment, bodyParams, apiLabel);
    return result;
  }

  /**
   * コメント更新
   * @param comment
   * @returns
   */
  public async updateComment(
    comment: Partial<Comment>,
  ): Promise<
    | { comment: DbComment; thumbs: FileSignedUrl[]; data: FileSignedUrl[] }
    | undefined
  > {
    const apiLabel = "updateComment";
    const requiredParams = {
      domainId: comment.domainId,
      topicId: comment.topicId,
      messageId: comment.messageId,
      id: comment.id,
    };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake(comment);
    const result = await this.putApi<{
      comment: DbComment;
      thumbs: FileSignedUrl[];
      data: FileSignedUrl[];
    }>(ServerApi.updateComment, bodyParams, apiLabel);
    return result;
  }

  /**
   * コメント一覧取得
   * @param domainId
   * @param topicId
   * @param messageId
   * @returns
   */
  public async listComments(
    domainId: string,
    topicId: string,
    messageId?: string,
    sort?: SORT_TYPE,
  ): Promise<{ comments: DbComment[] } | undefined> {
    const apiLabel = "listComments";
    const requiredParams = { domainId, topicId };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.getApi<{ comments: DbComment[] }>(
      ServerApi.listComments(domainId, topicId, messageId, sort),
      apiLabel,
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * リアクション作成/更新
   * @param reaction
   * @param value リアクションのチェック値
   * @returns
   */
  public async upsertReaction(
    reaction: DbReaction,
    value: boolean,
  ): Promise<DbReaction | undefined> {
    const apiLabel = "upsertReaction";
    const requiredParams = {
      type: reaction.type,
      targetType: reaction.targetType,
      domainId: reaction.domainId,
      topicId: reaction.topicId,
    };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake({
      ...reaction,
      value: value,
    });
    const result = await this.putApi<DbReaction>(
      ServerApi.upsertReaction,
      bodyParams,
      apiLabel,
    );
    return result;
  }

  /**
   * リアクション一覧取得
   * @param domainId
   * @param topicId
   * @returns
   */
  public async listReactions(
    domainId: string,
    topicId?: string,
  ): Promise<
    | {
        topicReactions: IconReaction[];
        messageReactions: IconReaction[];
        commentReactions: IconReaction[];
      }
    | undefined
  > {
    const apiLabel = "listReactions";
    const requiredParams = { domainId };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.getApi<{
      topicReactions: IconReaction[];
      messageReactions: IconReaction[];
      commentReactions: IconReaction[];
    }>(ServerApi.listReactions(domainId, topicId), apiLabel);
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * ユーザ一覧取得
   * @param domainId
   * @returns
   */
  public async listUsers(domainId: string): Promise<User[] | undefined> {
    const apiLabel = "listUsers";
    const requiredParams = { domainId };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.getApi<User[]>(
      ServerApi.listUsers(domainId),
      apiLabel,
    );
    if (result) {
      const users = result.map((data) => {
        const user = User.createNotFoundUser();
        user.override(data);
        return user;
      });
      return users;
    } else {
      return undefined;
    }
  }

  /**
   * 自身の情報を取得
   * @returns
   */
  public async getMe(): Promise<User | undefined> {
    const result = await this.getApi<User>(ServerApi.getMe(), "getMe");
    if (result?.id && result.directId && result.name) {
      const user = User.createNotFoundUser();
      user.override(result);
      return user;
    } else {
      return undefined;
    }
  }

  /**
   * プロフィール画像取得
   * @param directId
   * @returns
   */
  public async getProfileIcon(directId: string): Promise<string | undefined> {
    try {
      const apiLabel = "getProfileIcon";
      const requiredParams = { directId };
      if (!this.validateRequiredParams(requiredParams, apiLabel))
        return undefined;

      const response = await axios.get<{ profileIcon: string }>(
        ServerApi.getProfileIcon(directId),
        { withCredentials: true },
      );
      if (response.status === 200) {
        const icon = response.data.profileIcon;
        return icon;
      } else {
        return undefined;
      }
    } catch (_err) {
      return undefined;
    }
  }

  /**
   * ファイル取得
   * @param pathParam
   * @param fileName
   * @param thumb
   * @returns
   */
  public async getFile(
    pathParam: {
      domainId: string;
      topicId: string;
      messageId?: string;
      commentId?: string;
    },
    fileName: string,
    thumb: boolean,
    contentType: string,
  ): Promise<FileSignedUrl | undefined> {
    const apiLabel = "getFile";
    const requiredParams = {
      domainId: pathParam.domainId,
      topicId: pathParam.topicId,
      fileName,
      thumb,
      contentType,
    };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.getApi<FileSignedUrl>(
      ServerApi.getFile(pathParam, fileName, thumb, contentType),
      apiLabel,
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * ファイル送信
   * @param domainId
   * @param topicId
   * @param fileParams
   * @returns
   */
  public async putFiles(
    domainId: string,
    topicId: string,
    fileParams: {
      messageId?: string;
      commentId?: string;
      fileName: string;
      contentType: string;
      size: number;
    }[],
  ): Promise<{ data: FileSignedUrl[]; thumb: FileSignedUrl[] } | undefined> {
    const apiLabel = "putFiles";
    const requiredParams = { domainId, topicId, files: fileParams };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.putApi<{
      data: FileSignedUrl[];
      thumb: FileSignedUrl[];
    }>(
      ServerApi.putFiles,
      { domain_id: domainId, topic_id: topicId, files: fileParams },
      apiLabel,
    );
    return result;
  }

  /**
   * ファイル削除
   * @param domainId
   * @param topicId
   * @param fileParams
   * @returns
   */
  public async deleteFiles(
    domainId: string,
    topicId: string,
    fileParams: { messageId?: string; commentId?: string; fileName: string }[],
  ): Promise<{ thumb: FileSignedUrl[]; data: FileSignedUrl[] } | undefined> {
    const apiLabel = "deleteFiles";
    const requiredParams = { domainId, topicId, files: fileParams };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.deleteApi<{
      thumb: FileSignedUrl[];
      data: FileSignedUrl[];
    }>(ServerApi.deleteFiles(domainId, topicId, fileParams), apiLabel);
    return result;
  }

  /**
   * 組織設定の作成/更新
   * @param domainSettings
   * @returns
   */
  public async upsertDomainSettings(domainSettings: Partial<DomainSettings>) {
    const apiLabel = "upsertDomainSettings";
    const requiredParams = { id: domainSettings.id };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake({
      ...domainSettings,
      domain_id: domainSettings.id,
    });
    const result = await this.putApi<DomainSettings>(
      ServerApi.upsertDomainSettings,
      bodyParams,
      apiLabel,
    );
    return result;
  }

  /**
   * 現在の組織設定を取得
   * @param domainId
   * @returns
   */
  public async getDomainSettings(domainId: string): Promise<Domain> {
    try {
      const apiLabel = "getDomainSettings";
      const requiredParams = { domainId };
      if (!this.validateRequiredParams(requiredParams, apiLabel))
        throw new Error();

      const result = await this.getApi<DomainSettings>(
        ServerApi.getDomainSettings(domainId),
        apiLabel,
      );
      if (result) {
        return Domain.createFrom(result);
      } else {
        return Domain.create({ domainId: domainId });
      }
    } catch (_err) {
      return Domain.create({ domainId: domainId });
    }
  }

  /**
   * allowFeatureの一覧取得
   * @returns
   */
  public async listFeatures(): Promise<
    { [keyword: string]: string } | undefined
  > {
    const result = await this.getApi<{ [keyword: string]: string }>(
      ServerApi.listFeatures(),
      "listFeatures",
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * ヘルプコンテンツの取得
   * @returns
   */
  public async getHelpContent(): Promise<
    { title: string; content: string } | undefined
  > {
    const result = await this.getApi<{ title: string; content: string }>(
      ServerApi.getHelpContent(),
      "getHelpContent",
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * 個人設定の取得
   * @returns
   */
  public async getPersonalSetting(): Promise<PersonalSetting | undefined> {
    const result = await this.getApi<PersonalSetting>(
      ServerApi.getPersonalSetting(),
      "getPersonalSetting",
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * 個人設定の保存
   * @param personalSetting
   * @returns
   */
  public async upsertPersonalSetting(
    personalSetting: Partial<PersonalSetting>,
  ): Promise<PersonalSetting | undefined> {
    const apiLabel = "upsertPersonalSetting";
    const requiredParams = { sortComments: personalSetting.sortComments };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake(personalSetting);
    const result = await this.putApi<PersonalSetting>(
      ServerApi.upsertPersonalSetting,
      bodyParams,
      apiLabel,
    );
    return result;
  }

  /**
   * PDF変換ステータスの取得
   * @param param
   * @returns
   */
  public async getPdfConverterStatus(
    param: PdfConverterParam,
  ): Promise<PdfConverterStatusResponse | undefined> {
    const apiLabel = "getPdfConverterStatus";
    const requiredParams = {
      domainId: param.domainId,
      topicId: param.topicId,
      messageId: param.messageId,
      fileName: param.fileName,
    };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake(param);
    const result = await this.postApi<PdfConverterStatusResponse>(
      ServerApi.getPdfConverterStatus,
      bodyParams,
      apiLabel,
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * PDF変換を開始
   * @param param
   */
  public async sendStartPdfConversion(param: PdfConverterParam): Promise<void> {
    const apiLabel = "sendStartPdfConversion";
    const requiredParams = {
      domainId: param.domainId,
      topicId: param.topicId,
      messageId: param.messageId,
      fileName: param.fileName,
    };
    if (!this.validateRequiredParams(requiredParams, apiLabel)) return;

    const bodyParams = this.convertKeysCamelToSnake(param);
    await this.postApi(ServerApi.sendStartPdfConversion, bodyParams, apiLabel);
  }

  /**
   * 変換されたPDFファイルの取得
   * @param param
   * @returns
   */
  public async getPdfAttachmentFile(
    param: PdfConverterParam,
  ): Promise<PdfAttachmentFileResponse | undefined> {
    const apiLabel = "getPdfAttachmentFile";
    const requiredParams = {
      domainId: param.domainId,
      topicId: param.topicId,
      messageId: param.messageId,
      fileName: param.fileName,
    };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const bodyParams = this.convertKeysCamelToSnake(param);
    const result = await this.postApi<PdfAttachmentFileResponse>(
      ServerApi.getPdfAttachmentFile,
      bodyParams,
      apiLabel,
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * 組織のトーク一覧を取得する
   * @param domainId 組織ID
   * @returns
   */
  public async getTalks(
    domainId: string,
  ): Promise<DirectTalksType | undefined> {
    const apiLabel = "getTalks";
    const requiredParams = { domainId };
    if (!this.validateRequiredParams(requiredParams, apiLabel))
      return undefined;

    const result = await this.getApi<DirectTalksType>(
      ServerApi.getTalks(domainId),
      apiLabel,
    );
    if (result) {
      return result;
    } else {
      return undefined;
    }
  }

  /**
   * direct access token の revoke 処理
   * @returns
   */
  public async revokeToken(): Promise<void> {
    const result = await this.postApi<boolean>(
      ServerApi.revokeToken,
      {},
      "revokeToken",
    );
    if (result) {
      return;
    } else {
      return undefined;
    }
  }

  /** ロガーに記録する */
  public static logging(command: string, params: any): void {
    const api = new ServerApiAccess();
    api.postApi<any>(
      ServerApi.logging,
      {
        command,
        params,
      },
      "logging",
    );
  }

  private static errorlog(label: string, status: unknown, err?: any): void {
    let msg = `[${label}] error code:${status}`;
    if (err) msg += `data:${JSON.stringify(err)}`;
    console.error(msg);
    sentry.sendSentryError(new Error(msg));
  }

  public static debugSentry(): void {
    const api = new ServerApiAccess();
    api.getApi(ServerApi.error, "debugSentry");
  }
}
